import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, of } from 'rxjs/index';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import {
    BsModalConfirmComponent,
    ConfirmDialogResult,
    createConfirmModalOptions
} from '../../core/modal/modal-confirm.component';
import { ReasonsSettingsActions } from './reasons-settings.actions';
import { ReasonsService } from './reasons.service';

@Injectable()
export class ReasonsSettingsEffects {
    constructor(
        private actions$: Actions,
        private reasonsService: ReasonsService,
        private modalService: BsModalService) {
    }

    getReasons$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ReasonsSettingsActions.getRemindersReasons),
            switchMap((action: any) =>
                this.reasonsService.getReasons(action.userId)
                    .pipe(
                        map((payload: any) =>
                            ReasonsSettingsActions.getRemindersReasonsSuccess({ payload})
                        ),
                        catchError(error =>
                            of(ReasonsSettingsActions.getRemindersReasonsFail({ error }))
                        )
                    )
            )
        )
    );

    saveReasons$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ReasonsSettingsActions.saveRemindersReasons),
            mergeMap(({testCount, reasonId, strategyId}) =>
                this.reasonsService.saveReasons(testCount, reasonId, strategyId)
                    .pipe(
                        map((payload: any) => {
                            if (testCount) {
                                const dialogMessage = `This mapping will affect ${payload.estimatedAffected} appointments.
                                    Are you sure you want to make this change?`;

                                return ReasonsSettingsActions.openConfirmDialog({
                                    dialogMessage,
                                    editedReasonId: reasonId, editedStrategyId: strategyId,
                                    okLabel: 'Yes', cancelLabel: 'No'
                                });
                            }

                            return ReasonsSettingsActions.getRemindersReasons(undefined);
                            }
                        ),
                        catchError(error =>
                            of(ReasonsSettingsActions.saveRemindersReasonsFail({ error, reasonId, strategyId }))
                        )
                    )
            )
        )
    );

    saveReasonsFailRetry$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(ReasonsSettingsActions.saveRemindersReasonsFail),
            map(({reasonId, strategyId}) => {
                    return ReasonsSettingsActions.openConfirmDialog({
                        dialogMessage: 'Saving Reason Strategy failed. Do you want to retry?',
                        editedReasonId: reasonId, editedStrategyId: strategyId,
                        okLabel: 'Retry', cancelLabel: 'Cancel'
                    });
                })
        )
    );

    openConfirmDialog$ = createEffect(() =>
        this.actions$
            .pipe(
                ofType(ReasonsSettingsActions.openConfirmDialog),
                switchMap(({dialogMessage, editedReasonId, editedStrategyId, okLabel, cancelLabel}) => {
                    const message = dialogMessage;
                    const title = 'Confirm';
                    const data: any = { editedReasonId , editedStrategyId };
                    const modalRef = this.modalService
                        .show(BsModalConfirmComponent, createConfirmModalOptions(title, message, okLabel, cancelLabel, data));

                    return modalRef.content.onClose;
                }),
                map((dialogResult: ConfirmDialogResult) => { // map dialog result into action (map function returns observable to action)
                    if (dialogResult.isConfirmed) {
                        return ReasonsSettingsActions
                            .saveRemindersReasons({ testCount: false,
                                                    reasonId: dialogResult.data.editedReasonId,
                                                    strategyId: dialogResult.data.editedStrategyId });
                    }

                    return ReasonsSettingsActions
                        .openConfirmDialogCancel();
                })
            )
    );

}
